import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Loader2, Pencil, Table2, Target, Undo2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AGENT_TURN_DONE_EVENT } from '../lib/agents';
import { columnLabel, formatFigure } from '../lib/model';
import type { FigureInput, ModelCell, SummaryFigure } from '../lib/types';
import { InlineError } from './InlineError';
import { ConflictNotice, OriginLine } from './Provenance';
import WorkingsModal from './WorkingsModal';

const CURRENCIES = ['GBP', 'AUD', 'NZD', 'USD', 'EUR'];

const inputClass = 'text-sm px-3 py-1.5 border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand';
const linkButton = 'inline-flex items-center gap-1.5 text-[13px] font-medium text-brand hover:text-brand-dark transition-colors';
const saveButton = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium rounded-lg bg-brand text-white hover:bg-brand-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors';

// An area's summary figures and what would produce the ones the record lacks (all
// areas' without `sectionId`). Reloaded after each agent turn, which can record figures.
export function useSectionFigures(sectionId?: string) {
    const { authFetch } = useAuth();
    const [figures, setFigures] = useState<SummaryFigure[] | null>(null);
    const [inputs, setInputs] = useState<FigureInput[] | null>(null);

    const load = useCallback(() => {
        const query = sectionId ? `?section_id=${encodeURIComponent(sectionId)}` : '';
        authFetch(`/figures${query}`)
            .then(r => r.ok ? r.json() : { figures: [], inputs: [] })
            .then(data => { setFigures(data.figures); setInputs(data.inputs); })
            .catch(() => { setFigures([]); setInputs([]); });
    }, [authFetch, sectionId]);

    useEffect(load, [load]);
    useEffect(() => {
        window.addEventListener(AGENT_TURN_DONE_EVENT, load);
        return () => window.removeEventListener(AGENT_TURN_DONE_EVENT, load);
    }, [load]);

    return { figures, inputs, reload: load };
}

export const figureCell = (figure: SummaryFigure): ModelCell | null => {
    const f = figure.origin?.figure;
    if (!f || figure.status === 'unknown') return null;
    return { key: f.key, concept: f.concept, period: f.period, basis: 'actual', value: f.value, kind: figure.status };
};

function useFigureWrite(concept: string, onSaved: () => void) {
    const { authFetch } = useAuth();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const run = async (path: string, init: RequestInit) => {
        setBusy(true);
        setError(null);
        try {
            const res = await authFetch(`/figures/${concept}${path}`, init);
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.detail || 'Could not save that figure');
            }
            onSaved();
            return true;
        } catch (e: any) {
            setError(e.message);
            return false;
        } finally {
            setBusy(false);
        }
    };
    const put = (path: string, body: object) => run(path, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    return {
        busy, error,
        save: (value: number | null, unit?: string) => put('', unit ? { value, unit } : { value }),
        saveTarget: (value: number | null) => put('/target', { value }),
        useTheirs: () => run('/use-source', { method: 'POST' }),
    };
}

// A number, with the currency where it is money and the record doesn't know which yet.
function FigureEntry({ label, unit, currency, busy, initial, onSave, onCancel }: {
    label: string; unit: string; currency: string | null; busy: boolean; initial?: number | null;
    onSave: (value: number, unit?: string) => void; onCancel?: () => void;
}) {
    const [value, setValue] = useState(initial != null ? String(initial) : '');
    const needsCurrency = unit === 'currency' && !currency;
    const [chosen, setChosen] = useState('');
    const ready = value.trim() !== '' && !Number.isNaN(Number(value)) && (!needsCurrency || chosen);
    return (
        <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
                type="number" step="any" value={value} aria-label={label}
                onChange={e => setValue(e.target.value)}
                className={`${inputClass} w-40`}
            />
            {needsCurrency && (
                <select value={chosen} onChange={e => setChosen(e.target.value)} aria-label="Currency" className={inputClass}>
                    <option value="">Currency...</option>
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
            )}
            <button onClick={() => onSave(Number(value), needsCurrency ? chosen : undefined)} disabled={busy || !ready} className={saveButton}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                Save
            </button>
            {onCancel && (
                <button onClick={onCancel} className="text-[13px] font-medium text-coolslate hover:text-graphite">Cancel</button>
            )}
        </div>
    );
}

export function FigureCard({ figure, onSaved }: { figure: SummaryFigure; onSaved: () => void }) {
    const { busy, error, save, saveTarget, useTheirs } = useFigureWrite(figure.id, onSaved);
    const [editing, setEditing] = useState(false);
    const [targeting, setTargeting] = useState(false);
    const [tracing, setTracing] = useState(false);
    const cell = figureCell(figure);
    if (!cell) return null;
    const unit = figure.concept.unit;
    // A line broken down by holder or class takes its figures from documents; a group
    // figure broken down by company can still be targeted.
    const brokenDown = !!figure.breakdown?.dimensions.some(d => d.id !== 'company');
    const conflict = figure.origin?.conflict;
    const theirs = conflict && typeof conflict.value === 'number'
        ? formatFigure({ ...cell, value: { amount: conflict.value, unit: cell.value.unit } }, unit)
        : String(conflict?.value ?? '');

    return (
        <div className="border border-softgrey/70 bg-offwhite/50 rounded-xl p-4">
            <p className="text-[13px] text-coolslate">{figure.concept.name}</p>
            <p className="text-sm text-graphite mt-0.5">
                {formatFigure(cell, unit)}
                <span className="text-[12px] text-coolslate"> · {columnLabel(cell.period)}</span>
            </p>
            <p className="text-[11px] text-coolslate mt-1 flex items-center gap-1">
                <OriginLine origin={figure.origin} status={figure.status} />
            </p>
            {figure.breakdown && <Breakdown figure={figure} />}

            <ConflictNotice origin={figure.origin} status={figure.status} theirs={theirs}
                onUseTheirs={async () => { await useTheirs(); }} />

            {figure.target && !targeting && (
                <p className="mt-2 text-[12px] text-coolslate flex items-center gap-1">
                    <Target className="w-3 h-3 shrink-0" />
                    Target: {formatFigure({ ...cell, value: figure.target.value }, unit)}
                    {figure.target.status && <TargetStatus status={figure.target.status} />}
                </p>
            )}
            {editing && (
                <FigureEntry
                    label={figure.concept.name} unit={unit} currency={figure.currency} busy={busy}
                    initial={figure.value}
                    onSave={async (v, u) => { if (await save(v, u)) setEditing(false); }}
                    onCancel={() => setEditing(false)}
                />
            )}
            {targeting && (
                <div>
                    <FigureEntry
                        label={`Target for ${figure.concept.name}`} unit={unit} currency={cell.value.unit ?? null}
                        busy={busy} initial={figure.target?.value.amount}
                        onSave={async v => { if (await saveTarget(v)) setTargeting(false); }}
                        onCancel={() => setTargeting(false)}
                    />
                    {figure.target && (
                        <button onClick={async () => { if (await saveTarget(null)) setTargeting(false); }}
                            disabled={busy} className="mt-2 text-[13px] font-medium text-coolslate hover:text-graphite">
                            Remove target
                        </button>
                    )}
                </div>
            )}

            {error && <div className="mt-3"><InlineError message={error} /></div>}

            {!editing && !targeting && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                    {figure.editable && (
                        <button onClick={() => setEditing(true)} className={linkButton}>
                            <Pencil className="w-3.5 h-3.5" />
                            {figure.status === 'derived' ? 'Correct this' : 'Change'}
                        </button>
                    )}
                    <button onClick={() => setTracing(true)} className={linkButton}>
                        <Table2 className="w-3.5 h-3.5" />
                        Underlying data
                    </button>
                    {!brokenDown && (
                        <button onClick={() => setTargeting(true)} className={linkButton}>
                            <Target className="w-3.5 h-3.5" />
                            {figure.target ? 'Change target' : 'Set a target'}
                        </button>
                    )}
                    {figure.status === 'stated' && (
                        <button onClick={() => useTheirs()} disabled={busy}
                            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-coolslate hover:text-graphite transition-colors">
                            <Undo2 className="w-3.5 h-3.5" />
                            Remove your figure
                        </button>
                    )}
                </div>
            )}

            {tracing && (
                <WorkingsModal cell={cell} unit={unit} organization={figure.origin!.figure!.organization}
                    onClose={() => setTracing(false)} />
            )}
        </div>
    );
}

export function TargetStatus({ status }: { status: 'ahead' | 'behind' }) {
    return (
        <span className={`ml-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${status === 'ahead'
            ? 'bg-sage/15 text-sage'
            : 'bg-amber-100 text-amber-800'}`}>
            {status === 'ahead' ? 'Ahead' : 'Behind'}
        </span>
    );
}

export function TargetCard({ figure }: { figure: SummaryFigure }) {
    const cell = figureCell(figure)!;
    const target = figure.target!;
    const unit = figure.concept.unit;
    const value = cell.value.amount ?? 0;
    const goal = target.value.amount ?? 0;
    const progress = !goal ? 0 : figure.concept.better === 'lower'
        ? (value <= goal ? 1 : goal / value)
        : Math.min(1, value / goal);
    const tone = target.status === 'behind' ? 'bg-amber-400' : target.status === 'ahead' ? 'bg-sage' : 'bg-brand';

    return (
        <div className="border border-softgrey rounded-xl p-4 bg-white">
            <div className="flex items-baseline justify-between gap-2">
                <b className="text-[17px] font-semibold text-graphite tabular-nums">{formatFigure(cell, unit)}</b>
                {target.status && <TargetStatus status={target.status} />}
            </div>
            <p className="text-[13px] text-coolslate mt-0.5">{figure.concept.name} · {columnLabel(cell.period)}</p>
            <div className="mt-2 h-1.5 rounded-full bg-softgrey/60 overflow-hidden">
                <i className={`block h-full ${tone}`} style={{ width: `${Math.round(Math.max(0, progress) * 100)}%` }} />
            </div>
            <p className="text-[12px] text-coolslate mt-2">
                Target: {formatFigure({ ...cell, value: target.value }, unit)}
            </p>
            <p className="text-[11px] text-coolslate mt-1 flex items-center gap-1">
                <OriginLine origin={figure.origin} status={figure.status} />
            </p>
        </div>
    );
}

// A figure broken down by its dimensions: who holds what, each row with its share
// of the total and where it came from.
function Breakdown({ figure }: { figure: SummaryFigure }) {
    const breakdown = figure.breakdown!;
    const unit = figure.concept.unit;
    const total = breakdown.rows.reduce((sum, r) => sum + (r.value.amount ?? 0), 0);
    return (
        <div className="mt-3 overflow-x-auto">
            <p className="text-[12px] text-coolslate mb-1">As at {columnLabel(breakdown.period)}</p>
            <table className="w-full text-[13px]">
                <thead>
                    <tr className="text-coolslate text-left">
                        {breakdown.dimensions.map(d => <th key={d.id} className="font-medium py-1 pr-4">{d.name}</th>)}
                        <th className="font-medium py-1 pr-4 text-right">{figure.concept.name}</th>
                        <th className="font-medium py-1 pr-4 text-right">Share</th>
                        <th className="font-medium py-1">From</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-softgrey/60">
                    {breakdown.rows.map((row, i) => {
                        const conflict = row.origin.conflict;
                        return (
                            <tr key={i} className="align-top">
                                {breakdown.dimensions.map(d => (
                                    <td key={d.id} className="py-1 pr-4 text-graphite">{row.members[d.id]}</td>
                                ))}
                                <td className="py-1 pr-4 text-right tabular-nums text-graphite">
                                    {formatFigure({ value: row.value } as ModelCell, unit)}
                                    {conflict && typeof conflict.value === 'number' && (
                                        <span className="block text-[11px] text-amber-800">
                                            {conflict.origin_name || 'Another document'} gives {conflict.value.toLocaleString()}
                                        </span>
                                    )}
                                </td>
                                <td className="py-1 pr-4 text-right tabular-nums text-coolslate">
                                    {total ? `${((row.value.amount ?? 0) / total * 100).toFixed(1)}%` : ''}
                                </td>
                                <td className="py-1 text-[12px] text-coolslate">{row.origin.origin_name ?? ''}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// A What's Next item for figures the record lacks: what would produce them.
export function InputCard({ input, onSaved }: { input: FigureInput; onSaved: () => void }) {
    const { busy, error, save } = useFigureWrite(input.concept ?? '', onSaved);
    const action = {
        connect: { to: '/connections/add', label: 'Add a connection' },
        map: { to: '/model', label: 'Open the Model' },
        document: { to: `/workspace/${input.section_id}?tab=documents`, label: 'Go to Documents' },
        enter: null,
    }[input.kind];

    return (
        <div className="border border-softgrey bg-white rounded-xl p-4">
            <p className="text-sm font-medium text-graphite">{input.title}</p>
            <p className="text-[13px] text-coolslate mt-0.5">{input.description}</p>
            {input.kind === 'enter' && (
                <FigureEntry
                    label={input.title} unit={input.input_type === 'currency' ? 'currency' : ''}
                    currency={input.currency ?? null} busy={busy}
                    onSave={(v, u) => save(v, u)}
                />
            )}
            {error && <div className="mt-3"><InlineError message={error} /></div>}
            {action && (
                <Link to={action.to} className={`mt-3 ${linkButton}`}>
                    {action.label}
                    <ArrowRight className="w-3.5 h-3.5" />
                </Link>
            )}
        </div>
    );
}
